import { Component, OnInit, Inject } from "@angular/core";
import { FormControl } from "@angular/forms";
import { Observable } from "rxjs";
import { map, startWith } from "rxjs/operators";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { EmployeeService } from "../employee.service";
import { Employee } from "../employee";
import { LoggingService } from "../../utils/logging.service";
import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

export interface TreeNode {
  employee_id: number;
  employee_name: string;
  initials: string;
  region: string;
  employeetype: string;
  e_mail: string;
  phone: string;
  supervisingrater: number;
  children: TreeNode[];
  expanded: boolean;
  descendantCount: number;
  matched: boolean;
  dimmed: boolean;
}

@Component({
  selector: "app-employee-tree",
  templateUrl: "./employee-tree.component.html",
  styleUrls: ["./employee-tree.component.css"],
})
export class EmployeeTreeComponent implements OnInit {
  // Active, non-"unassigned", non-subcontractor employees — used to build the
  // tree and populate the supervisor autocomplete candidates.
  allEmployees: Employee[] = [];
  // Full unfiltered list from the API. Used for looking up current
  // supervisors (which may be inactive / sub-contractors) and for walking the
  // reporting chain in the edit dialog.
  fullEmployees: Employee[] = [];
  roots: TreeNode[] = [];
  filteredRoots: TreeNode[] = [];
  isLoading = true;

  regionFilter: string = "";
  searchTerm: string = "";
  matchCount: number = 0;

  availableRegions: string[] = [];

  faChevronDown = faChevronDown;
  faChevronRight = faChevronRight;

  constructor(
    public employeeService: EmployeeService,
    public dialog: MatDialog,
    public log: LoggingService
  ) {}

  ngOnInit(): void {
    this.loadEmployees();
  }

  loadEmployees(): void {
    this.isLoading = true;
    this.employeeService.getEmployees().subscribe(
      (res) => {
        if (res && res.Employees) {
          this.fullEmployees = res.Employees;
          this.allEmployees = res.Employees.filter((e: Employee) => {
            if (e.inactiveemployee === true) {
              return false;
            }
            const name = (e.employee_name || "").toLowerCase();
            if (name.indexOf("unassigned") > -1) {
              return false;
            }
            const type = (e.employeetype || "").toLowerCase().trim();
            if (
              type.indexOf("sub-contractor") > -1 ||
              type.indexOf("subcontractor") > -1
            ) {
              return false;
            }
            return true;
          });
          this.buildRegionList();
          this.buildTree();
        }
        this.isLoading = false;
      },
      (err) => {
        this.isLoading = false;
        this.log.error("Error loading employees for tree: " + err.toString());
      }
    );
  }

  buildRegionList(): void {
    const regions = new Set<string>();
    for (const emp of this.allEmployees) {
      if (emp.region) {
        regions.add(emp.region.trim());
      }
    }
    this.availableRegions = Array.from(regions).sort();
  }

  buildTree(): void {
    const nodeMap: { [id: number]: TreeNode } = {};

    // Create nodes
    for (const emp of this.allEmployees) {
      nodeMap[emp.employee_id] = {
        employee_id: emp.employee_id,
        employee_name: (emp.employee_name || "").trim(),
        initials: emp.initials || "",
        region: (emp.region || "").trim(),
        employeetype: (emp.employeetype || "").trim(),
        e_mail: emp.e_mail || "",
        phone: emp.phone || "",
        supervisingrater: emp.supervisingrater,
        children: [],
        expanded: true,
        descendantCount: 0,
        matched: false,
        dimmed: false,
      };
    }

    // Wire parent/child
    const roots: TreeNode[] = [];
    for (const emp of this.allEmployees) {
      const node = nodeMap[emp.employee_id];
      const parent =
        emp.supervisingrater && emp.supervisingrater > 0
          ? nodeMap[emp.supervisingrater]
          : null;
      if (parent && parent.employee_id !== node.employee_id) {
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    }

    // Sort children alphabetically (skip the root level — we sort it below
    // by descendant count so the largest org tree sits at the top).
    const sortChildrenRecursive = (nodes: TreeNode[]) => {
      for (const n of nodes) {
        n.children.sort((a, b) =>
          a.employee_name.localeCompare(b.employee_name)
        );
        sortChildrenRecursive(n.children);
      }
    };
    sortChildrenRecursive(roots);

    // Count descendants
    const countDescendants = (node: TreeNode): number => {
      let count = 0;
      for (const child of node.children) {
        count += 1 + countDescendants(child);
      }
      node.descendantCount = count;
      return count;
    };
    for (const r of roots) {
      countDescendants(r);
    }

    // Sort roots by descendant count (largest first) so the main org tree
    // — Sam Galphin's — always appears at the top. Name is the tiebreaker.
    roots.sort((a, b) => {
      if (b.descendantCount !== a.descendantCount) {
        return b.descendantCount - a.descendantCount;
      }
      return a.employee_name.localeCompare(b.employee_name);
    });

    // Set default expanded state: only expand the root level so the entire
    // tree fits on screen. Users click to drill down into branches.
    const setDefaultExpandedState = (node: TreeNode, depth: number) => {
      node.expanded = depth === 0; // only roots start expanded
      for (const child of node.children) {
        setDefaultExpandedState(child, depth + 1);
      }
    };
    for (const r of roots) {
      setDefaultExpandedState(r, 0);
    }

    this.roots = roots;
    this.applyFilters();
  }

  applyFilters(): void {
    const term = (this.searchTerm || "").toLowerCase().trim();
    const region = (this.regionFilter || "").trim();

    this.matchCount = 0;

    // No filters → show the full tree as-is.
    if (!term && !region) {
      this.filteredRoots = this.roots;
      return;
    }

    const matchesNode = (node: TreeNode): boolean => {
      const nameMatch = term
        ? node.employee_name.toLowerCase().includes(term) ||
          (node.initials || "").toLowerCase().includes(term)
        : true;
      const regionMatch = region ? node.region === region : true;
      return nameMatch && regionMatch;
    };

    // Build a pruned copy of the tree containing only matching nodes and the
    // ancestor chains needed to reach them. Returns null when neither this node
    // nor any descendant matches, so the branch is dropped entirely. We copy
    // nodes (rather than mutate the originals) so the unfiltered tree is left
    // intact for when filters are cleared.
    const prune = (node: TreeNode): TreeNode | null => {
      const isMatch = matchesNode(node);

      const prunedChildren: TreeNode[] = [];
      for (const child of node.children) {
        const prunedChild = prune(child);
        if (prunedChild) {
          prunedChildren.push(prunedChild);
        }
      }

      if (!isMatch && prunedChildren.length === 0) {
        return null;
      }

      if (isMatch) {
        this.matchCount++;
      }

      return {
        ...node,
        matched: isMatch,
        dimmed: false,
        // Expand so every retained branch is visible by default.
        expanded: true,
        children: prunedChildren,
      };
    };

    const result: TreeNode[] = [];
    for (const r of this.roots) {
      const prunedRoot = prune(r);
      if (prunedRoot) {
        result.push(prunedRoot);
      }
    }
    this.filteredRoots = result;
  }

  onSearchChange(): void {
    this.applyFilters();
  }

  onRegionChange(): void {
    this.applyFilters();
  }

  clearFilters(): void {
    this.searchTerm = "";
    this.regionFilter = "";
    this.applyFilters();
  }

  toggleNode(node: TreeNode): void {
    node.expanded = !node.expanded;

    // Auto-scroll to the expanded node so newly visible children come into view
    if (node.expanded) {
      setTimeout(() => {
        const element = document.getElementById(`node-${node.employee_id}`);
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "center",
          });
        }
      }, 150); // delay allows Angular to render children first
    }
  }

  expandAll(): void {
    const recurse = (nodes: TreeNode[]) => {
      for (const n of nodes) {
        n.expanded = true;
        recurse(n.children);
      }
    };
    recurse(this.filteredRoots);
  }

  collapseAll(): void {
    const recurse = (nodes: TreeNode[]) => {
      for (const n of nodes) {
        n.expanded = false;
        recurse(n.children);
      }
    };
    recurse(this.filteredRoots);
  }

  scrollTree(direction: "left" | "right"): void {
    const container = document.querySelector(
      ".tree-container"
    ) as HTMLElement;
    if (container) {
      const scrollAmount = 300; // pixels to scroll per click
      const currentScroll = container.scrollLeft;
      const targetScroll =
        direction === "left"
          ? currentScroll - scrollAmount
          : currentScroll + scrollAmount;
      container.scrollTo({ left: targetScroll, behavior: "smooth" });
    }
  }

}

@Component({
  selector: "app-employee-tree-edit",
  templateUrl: "./employee-tree-edit-dialog.html",
  styleUrls: ["./employee-tree.component.css"],
})
export class EmployeeTreeEditDialogComponent implements OnInit {
  supervisorCtrl = new FormControl("");
  filteredSupervisors$: Observable<Employee[]>;
  selectedSupervisor: Employee | null = null;
  reportingChain: Employee[] = [];
  saving = false;

  constructor(
    public dialogRef: MatDialogRef<EmployeeTreeEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public employeeService: EmployeeService,
    public log: LoggingService
  ) {}

  ngOnInit(): void {
    // Use the full employee list (which includes inactive/sub-contractor
    // employees) to look up the current supervisor, so we always find them
    // even if they're filtered out of the visible tree.
    const lookupList: Employee[] =
      this.data.fullEmployees || this.data.allEmployees;

    const current = lookupList.find(
      (e: Employee) => e.employee_id === this.data.employee.supervisingrater
    );
    if (current) {
      this.selectedSupervisor = current;
      this.supervisorCtrl.setValue(current as any);
    }

    // Build the full reporting chain: this employee → supervisor → ...
    this.reportingChain = this.buildReportingChain(lookupList);

    this.filteredSupervisors$ = this.supervisorCtrl.valueChanges.pipe(
      startWith(""),
      map((value) => this.filterSupervisors(value))
    );
  }

  buildReportingChain(lookupList: Employee[]): Employee[] {
    const chain: Employee[] = [];
    const visited = new Set<number>();
    let currentId: number | undefined = this.data.employee.supervisingrater;

    while (currentId && currentId > 0 && !visited.has(currentId)) {
      visited.add(currentId);
      const sup = lookupList.find(
        (e: Employee) => e.employee_id === currentId
      );
      if (!sup) break;
      chain.push(sup);
      currentId = sup.supervisingrater;
    }

    return chain;
  }

  filterSupervisors(value: any): Employee[] {
    const term =
      typeof value === "string"
        ? value.toLowerCase()
        : (value && value.employee_name ? value.employee_name : "").toLowerCase();
    const selfId = this.data.employee.employee_id;
    // Exclude self and direct descendants to avoid cycles
    const descendantIds = this.employeeService.getEmployeeTree(
      this.data.allEmployees,
      selfId
    );
    const blocked = new Set<number>([selfId, ...descendantIds]);

    return this.data.allEmployees
      .filter(
        (e: Employee) =>
          !blocked.has(e.employee_id) &&
          (e.employee_name || "").toLowerCase().includes(term)
      )
      .slice(0, 50);
  }

  displaySupervisor(emp: Employee): string {
    return emp && emp.employee_name ? emp.employee_name.trim() : "";
  }

  onSelectSupervisor(emp: Employee): void {
    this.selectedSupervisor = emp;
  }

  clearSupervisor(): void {
    this.selectedSupervisor = null;
    this.supervisorCtrl.setValue("");
  }

  save(): void {
    this.saving = true;
    const newSupervisorId = this.selectedSupervisor
      ? this.selectedSupervisor.employee_id
      : null;
    const newSupervisorName = this.selectedSupervisor
      ? (this.selectedSupervisor.employee_name || "").trim()
      : null;

    this.employeeService
      .updateEmployeeSupervisor(
        this.data.employee.employee_id,
        newSupervisorId,
        newSupervisorName
      )
      .subscribe(
        (res) => {
          this.saving = false;
          if (!res.Error) {
            this.log.info(
              "Updated supervisor for " +
                this.data.employee.employee_name +
                " to " +
                (newSupervisorName || "(none)")
            );
            this.dialogRef.close({ updated: true });
          }
        },
        (err) => {
          this.saving = false;
          this.log.error(
            "Error updating supervisor: " + err.toString()
          );
        }
      );
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
